import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './LegalPages.css';

function Terms() {
  return (
    <div className="legal-page">
      <Navbar />
      
      <div className="legal-container">
        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="last-updated">Last Updated: April 12, 2026</p>
        </div>

        <div className="legal-content">
          <section className="legal-section">
            <h2>1. Agreement to Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you 
              and Milan AI ("Company," "we," "us," or "our") governing your access to and use of 
              the milan-ai.net website and the Milan AI creator marketplace platform (collectively, 
              the "Service").
            </p>
            <p>
              By accessing or using the Service, you agree to be bound by these Terms. If you 
              disagree with any part of these Terms, you may not access the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Description of Service</h2>
            <p>
              Milan AI is a creator marketplace platform that connects brands with content creators 
              for influencer marketing campaigns. Our Service includes:
            </p>
            <ul>
              <li>Creator discovery and matchmaking based on audience demographics and engagement metrics</li>
              <li>Campaign creation, management, and tracking tools</li>
              <li>Content submission and approval workflows</li>
              <li>Performance tracking and analytics</li>
              <li>Secure payment processing with escrow protection</li>
              <li>Performance-based payment calculations</li>
              <li>
                <strong>Performance Hub</strong> &mdash; a separate subscription product for brands to track
                Instagram metrics on creator lists they already own (uploaded via CSV). Performance Hub does
                <strong> not</strong> include creator discovery, escrow, content approvals or performance-based
                payouts; see Section 6.16 for its specific terms.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. User Accounts</h2>
            
            <h3>3.1 Registration</h3>
            <p>To use certain features of the Service, you must register for an account. You agree to:</p>
            <ul>
              <li>Provide accurate, current, and complete information during registration</li>
              <li>Maintain and promptly update your account information</li>
              <li>Maintain the security of your password and account</li>
              <li>Accept responsibility for all activities under your account</li>
              <li>Notify us immediately of any unauthorized use of your account</li>
            </ul>

            <h3>3.2 Account Types</h3>
            <ul>
              <li><strong>Creator Accounts:</strong> For individuals who create content and collaborate with brands</li>
              <li><strong>Brand Accounts:</strong> For businesses seeking to run influencer marketing campaigns</li>
            </ul>

            <h3>3.3 Eligibility</h3>
            <p>
              You must be at least 13 years old to create an account on Milan AI. By registering 
              or using our platform, you confirm that you meet this age requirement. Users 
              between 13 and 18 years of age should have consent from a parent or guardian 
              before using the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>4. Creator Terms</h2>
            
            <h3>4.1 Instagram Connection</h3>
            <p>Creators must connect a valid Instagram Business or Creator account to use the Service. By connecting your account, you:</p>
            <ul>
              <li>Authorize us to access your Instagram data as described in our Privacy Policy</li>
              <li>Confirm that your account complies with Instagram's Terms of Use</li>
              <li>Agree to keep your Instagram account in good standing</li>
            </ul>

            <h3>4.2 Campaign Participation</h3>
            <p>When you accept a campaign invitation, you agree to:</p>
            <ul>
              <li>Create and deliver content according to the campaign brief and deadlines</li>
              <li>Disclose sponsored content in compliance with FTC guidelines and local regulations</li>
              <li>Not engage in fraudulent activities such as buying fake followers or engagement</li>
              <li>Submit original content that does not infringe on third-party rights</li>
              <li>Publish approved content within the specified timeframe</li>
            </ul>

            <h3>4.3 Payment Terms</h3>
            <ul>
              <li>Fixed payments (60%) are released weekly during the monitoring period (1-4 weeks)</li>
              <li>Performance payments (40%) are released after monitoring completes</li>
              <li>You receive your full quoted amount; the 3.5% platform fee is paid separately by the brand on top of your quote</li>
              <li>Kill fees of 5% of fixed payment apply if a campaign is cancelled after content submission (the brand is charged 8.5% total — 5% to you + 3.5% platform cancellation fee)</li>
              <li>You are responsible for reporting income and paying applicable taxes</li>
            </ul>

            <h3>4.4 Content Ownership</h3>
            <p>
              You retain ownership of all content you create. However, by participating in campaigns, 
              you grant brands a license to use the content as specified in the campaign agreement.
            </p>
          </section>

          <section className="legal-section">
            <h2>5. Brand Terms</h2>
            
            <h3>5.1 Campaign Creation</h3>
            <p>When creating campaigns, you agree to:</p>
            <ul>
              <li>Provide accurate campaign details, requirements, and budgets</li>
              <li>Fund campaigns before inviting creators (escrow requirement)</li>
              <li>Review and respond to content submissions within reasonable timeframes</li>
              <li>Not request content that violates laws, regulations, or platform policies</li>
            </ul>

            <h3>5.2 Payment Obligations</h3>
            <ul>
              <li>Campaign funds are held in escrow until content is approved and published</li>
              <li>You agree to pay the full agreed amount for completed campaigns</li>
              <li>Detailed payment and refund terms are outlined in Section 6 below</li>
            </ul>

            <h3>5.3 Content Rights</h3>
            <p>
              Content rights are specified in individual campaign agreements. Standard licenses 
              grant you rights to use creator content for the specified campaign purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Payment & Refund Policy</h2>
            
            <h3>6.1 Escrow Payment System</h3>
            <p>
              Milan AI uses a secure escrow system to protect both brands and creators. When a 
              brand creates a campaign, the allocated budget is held in escrow until campaign 
              milestones are completed.
            </p>
            <ul>
              <li>Brands fund campaigns before inviting creators</li>
              <li>Funds are held securely until content is approved and published</li>
              <li>Payments are released according to the terms outlined below</li>
            </ul>

            <h3>6.2 Creator Payment Structure</h3>
            <p>Creator payments consist of two components:</p>
            <ul>
              <li><strong>Fixed Payment (60%):</strong> A guaranteed base amount released weekly during the monitoring period</li>
              <li><strong>Performance-Based Payment (40%):</strong> Additional payment based on actual post performance metrics (engagement, reach, etc.) measured over the monitoring period and released at the end</li>
              <li><strong>Platform Fee (3.5%):</strong> Deducted from both fixed and performance payments</li>
            </ul>

            <h3>6.3 Individual Creator Monitoring</h3>
            <p>
              Each creator's monitoring period starts when THEIR content goes live. This means:
            </p>
            <ul>
              <li>Creators who publish earlier start their monitoring earlier</li>
              <li>Weekly fixed payments are released based on each creator's individual timeline</li>
              <li>Performance payment is released when that specific creator's monitoring ends</li>
            </ul>

            <h3>6.4 Payment Release Timeline</h3>
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Milestone</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Content approved and published</td>
                  <td>Individual creator's monitoring period begins (1-4 weeks as selected by brand)</td>
                </tr>
                <tr>
                  <td>Each week of monitoring</td>
                  <td>1/N of fixed payment released (where N = total monitoring weeks)</td>
                </tr>
                <tr>
                  <td>Monitoring period completed</td>
                  <td>Performance-based payment released based on actual metrics</td>
                </tr>
                <tr>
                  <td>Unused performance budget</td>
                  <td>Remaining amount refunded to brand</td>
                </tr>
              </tbody>
            </table>

            <h3>6.5 Platform Fee & Non-Refundable Charges</h3>
            <p>
              When you make a payment through our platform:
            </p>
            <ul>
              <li><strong>Platform Fee (3.5%):</strong> Charged to brands upfront and added to escrow. This fee is <strong>non-refundable</strong> once the campaign starts.</li>
              <li><strong>Payment Gateway Fee (~2.36%):</strong> Charged by Razorpay on every transaction. This is separate from the platform fee.</li>
            </ul>
            <p className="highlight-box">
              <strong>Important:</strong> The 3.5% platform fee covers escrow management, performance tracking, 
              and secure payments. This fee is non-refundable once payment is made, as it covers services 
              already rendered.
            </p>

            <h3>6.6 Brand Refund Scenarios</h3>
            <table className="policy-table">
              <thead>
                <tr>
                  <th>Scenario</th>
                  <th>Refund to Brand</th>
                  <th>Notes</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Draft campaign (no payment)</td>
                  <td>N/A</td>
                  <td>No payment made, no refund needed</td>
                </tr>
                <tr>
                  <td>Campaign cancelled before any creator accepts</td>
                  <td><strong>Full creator allocation</strong></td>
                  <td>Platform fee (3.5%) is non-refundable</td>
                </tr>
                <tr>
                  <td>Campaign cancelled after acceptance, before work starts</td>
                  <td><strong>Full creator allocation</strong></td>
                  <td>Platform fee (3.5%) is non-refundable</td>
                </tr>
                <tr>
                  <td>Creator fails to deliver content</td>
                  <td><strong>Full creator allocation</strong></td>
                  <td>That creator's portion refunded</td>
                </tr>
                <tr>
                  <td>Content rejected after 3 revision rounds</td>
                  <td><strong>Allocation minus 8.5% kill fee</strong></td>
                  <td>Creator receives 5% of the fixed payment; 3.5% retained as platform cancellation fee</td>
                </tr>
                <tr>
                  <td>After content approved</td>
                  <td><strong>Cannot cancel</strong></td>
                  <td>Campaign must proceed to completion</td>
                </tr>
                <tr>
                  <td>Unused performance budget after completion</td>
                  <td><strong>100%</strong> of unused portion</td>
                  <td>Full refund for unused performance allocation</td>
                </tr>
              </tbody>
            </table>
            
            <h3>6.7 Example Refund Calculation</h3>
            <p>
              <strong>Example:</strong> If your creator allocation totals ₹10,000, you'll be charged ₹10,350 at checkout
              (₹10,000 to creators + ₹350 platform fee on top):
            </p>
            <ul>
              <li><strong>Cancel before acceptance:</strong> ₹10,000 refunded (platform fee ₹350 non-refundable)</li>
              <li><strong>Cancel after work starts:</strong> Refund depends on work completed + kill fees</li>
              <li><strong>Unused performance:</strong> 100% of unused amount refunded</li>
            </ul>

            <h3>6.8 Creator Compensation (Kill Fees)</h3>
            <p>
              Creators receive partial compensation when campaigns are cancelled after they have submitted content:
            </p>
            <ul>
              <li><strong>Content rejected after maximum revisions (3 rounds):</strong> Creator receives <strong>5% of fixed payment</strong> as kill fee. Brand is charged 8.5% total (5% creator + 3.5% platform cancellation fee).</li>
              <li><strong>Backup creator activated:</strong> Receives remaining allocation from replaced creator</li>
              <li><strong>No delivery:</strong> No payment to creator</li>
              <li><strong>Late delivery beyond grace period:</strong> Campaign may be cancelled with no payment</li>
            </ul>

            <h3>6.9 Content Review & Revision Policy</h3>
            <ul>
              <li><strong>Revision Limit:</strong> Creators are allowed up to 3 rounds of revisions per content piece</li>
              <li><strong>Review Timeline:</strong> Brands must review and respond to submissions within 72 hours</li>
              <li><strong>Auto-Approval:</strong> If a brand does not respond within 72 hours, the content is automatically approved</li>
              <li><strong>Final Rejection:</strong> If content is still unsatisfactory after 3 revisions, the campaign for that creator is cancelled with kill fee compensation — 5% of fixed payment goes to the creator (3.5% retained as platform cancellation fee)</li>
            </ul>

            <h3>6.10 Platform Fee Structure</h3>
            <ul>
              <li>Platform fee is <strong>3.5%</strong> charged to brands at the time of payment (added to escrow)</li>
              <li>Platform fee of <strong>3.5%</strong> is also deducted from creator payments (both fixed and performance)</li>
              <li>This fee covers platform operations, escrow protection, performance tracking, and secure payments</li>
              <li><strong>Platform fee is non-refundable</strong> once the campaign payment is made</li>
            </ul>

            <h3>6.11 Partial Delivery</h3>
            <p>
              If a creator delivers only a portion of the required content (e.g., 2 out of 3 posts):
            </p>
            <ul>
              <li>Pro-rata payment is made for approved and published content</li>
              <li>Remaining undelivered portion is refunded to the brand</li>
            </ul>

            <h3>6.12 Creator Withdrawal</h3>
            <p>
              If a creator accepts a campaign but withdraws before completing deliverables:
            </p>
            <ul>
              <li>No payment is made to the creator</li>
              <li>Full refund is issued to the brand for that creator's allocation</li>
              <li>A backup creator may be activated with the remaining allocation</li>
              <li>Repeated withdrawals may result in account suspension or reduced visibility on the platform</li>
            </ul>

            <h3>6.13 Dispute Resolution</h3>
            <p>
              In case of disagreements between brands and creators regarding content quality or 
              campaign requirements:
            </p>
            <ul>
              <li>Either party may raise a dispute through the platform</li>
              <li>Milan AI will review the campaign brief, submitted content, and communication</li>
              <li>Milan AI's decision on disputes is final and binding</li>
              <li>Disputes must be raised within 14 days of the issue occurring</li>
            </ul>

            <h3>6.14 Refund Processing</h3>
            <ul>
              <li>Refunds are processed within 5-7 business days via Razorpay</li>
              <li>Refunds are credited to the original payment method</li>
              <li>You will receive an email confirmation when refund is processed</li>
              <li>Platform fee (3.5%) is non-refundable once payment is made</li>
            </ul>

            <h3>6.15 Account Deletion & Pending Payments</h3>
            <p>
              If you request account deletion while you have pending payments:
            </p>
            <ul>
              <li><strong>For Creators:</strong> Any pending payments that have not yet been processed will be forfeited. We recommend waiting until all your payments are fully processed before requesting account deletion.</li>
              <li><strong>For Brands:</strong> Any funds held in escrow for active campaigns will be handled according to the campaign status and refund policies above.</li>
            </ul>
            <p className="highlight-box">
              <strong>Important:</strong> Account deletion is irreversible. Please ensure all your 
              pending transactions are complete before submitting a deletion request. Once your 
              account is deleted, we cannot process any outstanding payments.
            </p>

            <h3>6.16 Performance Hub Subscriptions (Brands)</h3>
            <p>
              Performance Hub is an <strong>additional, optional product</strong> for brands. It is sold as a
              monthly subscription and is governed by these terms in addition to the rest of Section 6 where
              applicable. The escrow rules, the 3.5% platform fee, kill fees, and performance-based payout
              schedules described above in Section 6 apply <strong>only</strong> to Milan campaigns and do
              <strong> not</strong> apply to Performance Hub.
            </p>

            <h4>6.16.1 Tiers, Caps and Fees</h4>
            <ul>
              <li>
                <strong>Free tier:</strong> Limited number of active Performance Hub campaigns and connected
                creators, as published on our Pricing page. No payment is required.
              </li>
              <li>
                <strong>Pro / Enterprise tiers:</strong> Higher caps on active campaigns and connected creators,
                CSV export, and other paid features as listed on the Pricing page. Prices are shown in INR
                exclusive of applicable taxes and Razorpay processing fees.
              </li>
              <li>
                Each Performance Hub checkout grants access for the <strong>billing period specified at
                purchase</strong> (typically one month). The subscription does <strong>not</strong> auto-renew
                today &mdash; you must complete a new checkout to extend or upgrade. We may introduce
                auto-renewal in the future with prior notice.
              </li>
              <li>
                Plan caps are enforced at the workspace level. If we change published caps, your then-active
                paid period will continue to honour the caps in effect at the time of purchase.
              </li>
            </ul>

            <h4>6.16.2 Refunds</h4>
            <ul>
              <li>
                Performance Hub subscription fees are <strong>non-refundable</strong> once the subscription
                has been activated, except where required by applicable law.
              </li>
              <li>
                If we suspend or terminate Performance Hub access for reasons other than a Section 7
                violation, you may request a <strong>pro-rata refund</strong> of the unused portion of the
                current billing period. Razorpay payment-gateway charges are not refundable.
              </li>
              <li>
                Downgrades or cancellations take effect at the end of the current billing period; we do not
                provide partial refunds for early downgrades initiated by you.
              </li>
            </ul>

            <h4>6.16.3 Brand-Provided Creator Contact Lists</h4>
            <p>
              When you upload a CSV of Instagram handles, emails, and post URLs to Performance Hub, you act
              as the <strong>data controller</strong> for that contact list and you confirm that:
            </p>
            <ul>
              <li>
                You have a <strong>lawful basis</strong> (consent, legitimate interest, prior business
                relationship, or another permitted ground under applicable law including the DPDP Act) to
                share each creator&rsquo;s email address with Milan AI for the limited purpose of sending an
                Instagram-connect invitation on your behalf.
              </li>
              <li>
                Milan AI acts as a <strong>data processor</strong> for the uploaded contact list and will use
                those emails only to (a) send the initial connect invitation, (b) send resends that you
                explicitly trigger, and (c) operate the Performance Hub product. We will not use the list to
                market other Milan products to those creators without their independent opt-in.
              </li>
              <li>
                You will respect <strong>opt-outs</strong>. If a creator declines, ignores, or revokes consent,
                you will not re-invite them to the same campaign without a fresh lawful basis.
              </li>
              <li>
                You are responsible for the <strong>accuracy</strong> of the data you upload, for honouring any
                contracts you have with the listed creators, and for any payments owed to them. Milan AI does
                not act as an intermediary or payment agent for Performance Hub creators.
              </li>
            </ul>

            <h4>6.16.4 Instagram Connections via Magic Link</h4>
            <ul>
              <li>
                Creators connect their Instagram Business or Creator accounts to Performance Hub through an
                OAuth magic link. Connection is voluntary and revocable at any time from Instagram&rsquo;s
                &ldquo;Apps and Websites&rdquo; settings.
              </li>
              <li>
                Metric sync relies on Instagram&rsquo;s Graph API. <strong>Instagram-side outages, rate
                limits, scope revocations, or account suspensions</strong> can pause or interrupt sync. These
                events are outside our control and are not, by themselves, grounds for a refund.
              </li>
            </ul>

            <h4>6.16.5 Acceptable Use</h4>
            <ul>
              <li>
                Performance Hub is for tracking Instagram performance of creators with whom you have a real
                business relationship. It is <strong>not</strong> a tool for unsolicited mass outreach, paid
                follower farms, scraping, or bypassing Instagram&rsquo;s Platform Policies.
              </li>
              <li>
                Repeated complaints, high invite bounce rates, or detected abuse may result in suspension of
                Performance Hub access (with pro-rata refund per 6.16.2) and/or wider Section 7 enforcement.
              </li>
            </ul>

            <h4>6.16.6 No Escrow, No Payouts, No Performance Payments via Performance Hub</h4>
            <p>
              Performance Hub is a measurement and coordination product only. Milan AI does <strong>not</strong>
              hold escrow funds, process creator payouts, or compute performance-based payments for creators
              tracked via Performance Hub. Any compensation arrangements between you and those creators are
              solely your responsibility.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Prohibited Activities</h2>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Service for any illegal purpose or in violation of any laws</li>
              <li>Impersonate any person or entity or misrepresent your affiliation</li>
              <li>Engage in fraud, including fake followers, fake engagement, or fake reviews</li>
              <li>Attempt to gain unauthorized access to any part of the Service</li>
              <li>Interfere with or disrupt the Service or servers</li>
              <li>Scrape, data mine, or collect data from the Service without permission</li>
              <li>Use automated systems to access the Service without authorization</li>
              <li>Transmit viruses, malware, or other harmful code</li>
              <li>Harass, abuse, or harm other users</li>
              <li>Post content that is defamatory, obscene, or promotes illegal activities</li>
              <li>Circumvent or manipulate platform fees or payment systems</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>8. Intellectual Property</h2>
            
            <h3>8.1 Platform IP</h3>
            <p>
              The Service, including its original content, features, and functionality, is owned 
              by Milan AI and protected by copyright, trademark, and other intellectual property laws.
            </p>

            <h3>8.2 User Content</h3>
            <p>
              You retain all rights to content you submit to the Service. By submitting content, 
              you grant us a non-exclusive, worldwide, royalty-free license to use, display, and 
              distribute such content in connection with operating the Service.
            </p>
          </section>

          <section className="legal-section">
            <h2>9. Privacy & Data Protection</h2>
            <p>
              We take your privacy seriously and are committed to protecting your personal 
              information. Our <a href="/privacy-policy">Privacy Policy</a> provides detailed 
              information about how we collect, use, store, and safeguard your data, including 
              information obtained from connected social media accounts like Instagram.
            </p>
            <p>
              By using our Service, you acknowledge that you have read and understood our 
              Privacy Policy and consent to the data practices described therein.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Platform Availability & Modifications</h2>
            
            <h3>10.1 Service Availability</h3>
            <p>
              While we work to maintain reliable access to our platform, we cannot promise 
              uninterrupted availability at all times. Planned maintenance windows will be 
              announced to users in advance whenever possible. Milan AI shall not be held 
              responsible for temporary outages or service disruptions beyond our control.
            </p>

            <h3>10.2 Platform Updates</h3>
            <p>
              We reserve the right to update, modify, or enhance the platform's features, 
              functionality, pricing structure, or these Terms at any time. Users will be 
              notified of significant changes through email or platform announcements. Your 
              continued use of the Service following such changes indicates your acceptance 
              of the updated terms.
            </p>

            <h3>10.3 Feature Discontinuation</h3>
            <p>
              Milan AI may choose to retire or discontinue certain features or services. 
              In such cases, we will provide reasonable advance notice to allow users to 
              adjust their workflows accordingly.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Disclaimers</h2>
            <p className="highlight-box">
              THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, 
              WHETHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED, 
              SECURE, OR ERROR-FREE.
            </p>
            <ul>
              <li>We do not guarantee specific campaign results or creator performance</li>
              <li>We are not responsible for disputes between brands and creators</li>
              <li>We do not endorse or verify the accuracy of user-provided information</li>
              <li>Instagram metrics are provided by Instagram and may have limitations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>12. Limitation of Liability</h2>
            <p>
              TO THE MAXIMUM EXTENT PERMITTED BY LAW, MILAN AI SHALL NOT BE LIABLE FOR ANY 
              INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT 
              NOT LIMITED TO LOSS OF PROFITS, DATA, USE, OR GOODWILL, ARISING OUT OF OR IN 
              CONNECTION WITH THESE TERMS OR YOUR USE OF THE SERVICE.
            </p>
            <p>
              Our total liability for any claims arising out of or relating to these Terms or 
              the Service shall not exceed the total amount you have paid to us in the twelve 
              months preceding the claim.
            </p>
          </section>

          <section className="legal-section">
            <h2>13. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless Milan AI and its officers, 
              directors, employees, and agents from any claims, damages, losses, liabilities, 
              and expenses (including attorneys' fees) arising out of or related to:
            </p>
            <ul>
              <li>Your use of the Service</li>
              <li>Your violation of these Terms</li>
              <li>Your violation of any third-party rights</li>
              <li>Content you submit to the Service</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>14. Termination</h2>
            
            <h3>14.1 By You</h3>
            <p>
              You may terminate your account at any time by contacting us or using the account 
              deletion feature. Pending campaigns and payments will be handled according to our 
              cancellation policies.
            </p>

            <h3>14.2 By Us</h3>
            <p>
              We may suspend or terminate your account immediately, without prior notice, if you 
              violate these Terms or engage in conduct that we determine, in our sole discretion, 
              is harmful to the Service or other users.
            </p>

            <h3>14.3 Effect of Termination</h3>
            <p>
              Upon termination, your right to use the Service will immediately cease. Provisions 
              of these Terms that should survive termination shall survive, including ownership 
              provisions, warranty disclaimers, indemnity, and limitations of liability.
            </p>
          </section>

          <section className="legal-section">
            <h2>15. Dispute Resolution</h2>
            
            <h3>15.1 Between Users</h3>
            <p>
              We provide mediation services for disputes between brands and creators. However, 
              we are not responsible for resolving all disputes and encourage users to communicate 
              directly when possible.
            </p>

            <h3>15.2 With Milan AI</h3>
            <p>
              Any disputes with Milan AI shall first be addressed through our support channels. 
              If not resolved, disputes shall be subject to binding arbitration in accordance 
              with applicable arbitration rules.
            </p>
          </section>

          <section className="legal-section">
            <h2>16. Changes to Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. We will notify you of 
              material changes by email or through a notice on our Service. Your continued use 
              of the Service after such changes constitutes acceptance of the new Terms.
            </p>
          </section>

          <section className="legal-section">
            <h2>17. General Provisions</h2>
            <ul>
              <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Milan AI regarding the Service.</li>
              <li><strong>Severability:</strong> If any provision is found unenforceable, the remaining provisions will continue in effect.</li>
              <li><strong>Waiver:</strong> Our failure to enforce any right or provision shall not constitute a waiver.</li>
              <li><strong>Assignment:</strong> You may not assign these Terms without our prior written consent.</li>
              <li><strong>Governing Law:</strong> These Terms shall be governed by applicable laws.</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>18. Contact Us</h2>
            <p>
              If you have questions about these Terms, please reach out to us through 
              our <a href="/contact">Contact Page</a>.
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Terms;
